import * as React from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { hexToRgb } from '../Constants/Layout';
import ToastsProvider from "./ToastsProvider";
import useMediaQuery from "../Hooks/useMediaQuery";
import DevMenu from "../Components/Actions/DevMenu";
import {
	ContentOnlyLayout,
	HeaderLayout,
	HeaderSidebarLayout,
	SidebarHeaderLayout,
	SidebarLayout
} from '../Constants/LayoutWrappers';
import {AuthContext} from "react-oauth2-code-pkce";

export const drawerPortalEl : any = document.getElementById('drawerPortal');
export const modalPortalEl : any = document.getElementById('modalPortal');
export const menuPortalEl : any = document.getElementById('menuPortal');
export const notificationsPortalEl : any = document.getElementById('notificationsPortal');

export enum Layouts {
	DEFAULT = 'default',
	HEADER_LAYOUT = 'header-layout',
	SIDEBAR_LAYOUT = 'sidebar-layout',
	HEADER_SIDEBAR ='header-sidebar-layout',
	SIDEBAR_HEADER ='sidebar-header-layout',
	CONTENT_ONLY ='content-only',
}

interface iLayoutProvider {
	toggleCompacter: () => void;
	toggleSidebar: () => void;
	toggleTheme: () => void;
	toggleDevDebugger: () => void;
	toggleReducedMotion: () => void;

	updateLoading: (input: boolean, section: number) => void;
	updatePageSection: (input: number) => void;
	updateDirection: (direction: string) => void;
	updatePageColour: (colour: string) => void;
	updateBodyOverride: (input: string) => void;
	updateError: (input: boolean) => void;
	updateOverlayOpen: (input: boolean) => void;

	bodyOverride: string;
	pageColour: {};
	theme: string;
	direction: string;
	overlayOpen: boolean;
	devDebugger: boolean;
	reducedMotion: boolean;
	compacter: boolean;
	sidebarOpen: boolean;
	loading: {};
	error: boolean;
	pageSection: number;
	subMenusStatus: boolean;

	setContentOnlyLayout: (val?: boolean) => void;
	setHeaderSidebarLayout: (val?: boolean) => void;
	setSidebarHeaderLayout: (val?: boolean) => void;
	setHeaderLayout: (val?: boolean) => void;
	setSidebarLayout: (val?: boolean) => void;
	setDefaultLayout: (val?: boolean) => void;
	setSubMenusStatus?: any;

	closeAllOpened: () => void;
	closeChildMenus: () => void;

	addGlobalDocumentValue: (name: string, value: any) => void;
	removeGlobalDocumentValue: (name: string) => void;

	isTiny: boolean;
	isSmall: boolean;
	isMedium: boolean;
	isLarge: boolean;
	isXL: boolean;
	isXXL: boolean;
	isUltraWide: boolean;
}

const LayoutContext = React.createContext<iLayoutProvider>({} as iLayoutProvider);

const LayoutProvider = (props: any) => {
	const [isTiny, isSmall, isMedium, isLarge, isXL, isXXL, isUltraWide] = useMediaQuery(['(max-width: 480px)', '(min-width: 480px)', '(min-width: 640px)', '(min-width: 1024px)', '(min-width: 1280px)', '(min-width: 1920px)', '(min-width: 2700px)',]);

	const [ pageColour, setPageColour ] = useState<{}>({ hex: '#080808', rgb: hexToRgb('#080808') });
	const [ theme, setTheme ] = useState<string>('light');
	const [ devDebugger, setDevDebugger ] = useState<boolean>(false);
	const [ reducedMotion, setReducedMotion ] = useState<boolean>(false);
	const [ loading, setLoading ] = useState<{}>({ state: false, section: 0 });
	const [ pageSection, setPageSection ] = useState<number>(0);
	const [ compacter, setCompacter ] = useState<boolean>(false);
	const [ bodyOverride, setBodyOverride ] = useState<string>('');
	const [ error, setError ] = useState<boolean>(false);
	const [ sidebarOpen, setSidebarOpen ] = useState<boolean>(false);
	const [ layoutName, setLayoutName ] = useState<string>(props.defaultLayout || Layouts.SIDEBAR_LAYOUT);
	const [ direction, setDirection ] = useState<string>('ltr');
	const [ subMenusStatus, setSubMenusStatus ] = useState<any>([]);

	const [ overlayOpen, setOverlayOpen] = useState<boolean>(false);

	useEffect(() => {
		if (localStorage.getItem('ukpb-theme')) {
			if (localStorage.getItem('ukpb-theme') === 'dark') {
				updateTheme('dark');
			}
		} else if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
			updateTheme('dark');
		} else {
			updateTheme('light');
		}

		updateSidebar(localStorage.getItem(
			'ukpb-navigation-open') ? localStorage.getItem('ukpb-navigation-open') === 'true' : true
		);

		if (localStorage.getItem('ukpb-reduced-motion')) {
			updateReducedMotion(localStorage.getItem('ukpb-reduced-motion') === 'true');
		}
		if (localStorage.getItem('ukpb-dev-debugger')) {
			updateDevDebugger(localStorage.getItem('ukpb-dev-debugger') === 'true');
		}
		if (localStorage.getItem('ukpb-compacter')) {
			updateCompacter(localStorage.getItem('ukpb-compacter') === 'true');
		}
		if (localStorage.getItem('ukpb-direction')) {
			updateDirection(localStorage.getItem('ukpb-direction') as string);
		}
		if (localStorage.getItem('ukpb-layout')) {
			setLayoutName(localStorage.getItem('ukpb-layout') as string);
		}
	}, []);

	useEffect(() => {
		updateBodyOverride(error ? 'error' : '');
	}, [error]);

	const toggleSidebar = () => {
		updateSidebar(!sidebarOpen);
	};

	const addGlobalDocumentValue = (name: string, value: any) => {
		document.documentElement.setAttribute(`data-${name}`, value.toString());
	}

	const removeGlobalDocumentValue = (name: string) => {
		document.documentElement.removeAttribute(`data-${name}`);
	}

	const updateSidebar = (state: boolean) => {
		setSidebarOpen(state);
		localStorage.setItem('ukpb-navigation-open', state.toString());
		document.documentElement.setAttribute('data-menu-open', state.toString());
	};

	const toggleReducedMotion = () => {
		updateReducedMotion(!reducedMotion);
	};

	const updateReducedMotion = (state: boolean) => {
		setReducedMotion(state);
		localStorage.setItem('ukpb-reduced-motion', state.toString());
		document.documentElement.setAttribute('data-reduced-motion', state.toString());
	};

	const toggleDevDebugger = () => {
		updateDevDebugger(!devDebugger);
	};

	const updateDevDebugger = (state: boolean) => {
		setDevDebugger(state);
		localStorage.setItem('ukpb-dev-debugger',  state.toString());
		document.documentElement.setAttribute('data-dev-debugger', state.toString());
	};

	const toggleCompacter = () => {
		updateCompacter(!compacter);
	};

	const updateCompacter = (state: boolean) => {
		setCompacter(state);
		localStorage.setItem('ukpb-compacter',  state.toString());
		document.documentElement.setAttribute('data-compacter', state.toString());
	};

	const toggleTheme = () => {
		updateTheme(theme === 'light' ? 'dark' : 'light');
	};

	const updateTheme = (theme: string) => {
		//setTheme(theme);
		//localStorage.setItem('ukpb-theme', theme);
		//document.documentElement.setAttribute('data-theme', theme);
	};

	const updateDirection = (direction: string) => {
		setDirection(direction);
		localStorage.setItem('ukpb-direction', direction);
		document.documentElement.setAttribute('dir', direction);
	};

	const updatePageColour = (colour: string) => {
		setPageColour({ hex: colour, rgb: hexToRgb(colour) });
	};

	const updatePageSection = (input: number) => {
		setPageSection(input);
	};

	const updateBodyOverride = (input: string) => {
		setBodyOverride(input);
		document.documentElement.className = input;
	};

	const updateError = (input: boolean) => {
		setError(input);
	};

	const updateLoading = (input: boolean, section: number) => {
		setLoading({ state: input, section: section });
	};

	const updateOverlayOpen = (input: boolean) => {
		setOverlayOpen(input);
		document.documentElement.setAttribute('data-overlay-open', input.toString());

		if(input) {
			document.body.style.position = 'fixed';
			document.body.style.top = `-${window.scrollY}px`;
		} else {
			const scrollY = document.body.style.top;
			document.body.style.position = '';
			document.body.style.top = '';
			window.scrollTo(0, parseInt(scrollY || '0') * -1);
		}
	}

	const setHeaderLayout = (onceOnly?: boolean) => {
		if(!onceOnly) {
			localStorage.setItem('ukpb-layout', 'header-layout');
		}
		setLayoutName(Layouts.HEADER_LAYOUT);
	}

	const setSidebarLayout = (onceOnly?: boolean) => {
		if(!onceOnly) {
			localStorage.setItem('ukpb-layout', 'sidebar-layout');
		}
		setLayoutName(Layouts.SIDEBAR_LAYOUT);
	}

	const setContentOnlyLayout = (onceOnly?: boolean) => {
		if(!onceOnly) {
			localStorage.setItem('ukpb-layout', 'content-only');
		}
		setLayoutName(Layouts.CONTENT_ONLY);
	}

	const setHeaderSidebarLayout = (onceOnly?: boolean) => {
		if(!onceOnly) {
			localStorage.setItem('ukpb-layout', 'header-sidebar-layout');
		}
		setLayoutName(Layouts.HEADER_SIDEBAR);
	}

	const setSidebarHeaderLayout = (onceOnly?: boolean) => {
		if(!onceOnly) {
			localStorage.setItem('ukpb-layout', 'sidebar-header-layout');
		}
		setLayoutName(Layouts.SIDEBAR_HEADER);
	}

	const setDefaultLayout = (onceOnly?: boolean) => {
		if(!onceOnly) {
			localStorage.setItem('ukpb-layout', 'sidebar-layout');
		}
		setLayoutName(Layouts.DEFAULT);
	}

	const closeAllOpened = () => {
		document.querySelectorAll('.open, .child-menu-open').forEach((opened: any) => {
			opened.classList.remove('open');
			setSubMenusStatus([]);
		});
	}

	const closeChildMenus = () => {
		setSubMenusStatus([]);
	}

	const formMemo = useMemo(() => {
		return {
			loading,
			bodyOverride,
			pageColour,
			pageSection,
			theme,
			devDebugger,
			reducedMotion,
			compacter,
			error,
			sidebarOpen,
			layoutName,
			direction,
			overlayOpen,
			addGlobalDocumentValue,
			removeGlobalDocumentValue,
			updatePageColour,
			updatePageSection,
			updateBodyOverride,
			updateError,
			updateLoading,
			updateDirection,
			updateOverlayOpen,
			toggleTheme,
			toggleDevDebugger,
			toggleReducedMotion,
			toggleSidebar,
			toggleCompacter,
			setContentOnlyLayout,
			setHeaderSidebarLayout,
			setSidebarHeaderLayout,
			setHeaderLayout,
			setSidebarLayout,
			setDefaultLayout,
			closeAllOpened,
			closeChildMenus,
			setSubMenusStatus,
			subMenusStatus,
			isTiny,
			isSmall,
			isMedium,
			isLarge,
			isXL,
			isXXL,
			isUltraWide
		};
	}, [subMenusStatus, isTiny, isSmall, isMedium, isLarge, isXL, isXXL, isUltraWide, layoutName, direction, overlayOpen, compacter, reducedMotion, sidebarOpen, devDebugger, bodyOverride, pageColour, pageSection, error, theme]);

	const MapLayout = useCallback((props: any) => {
		switch(layoutName) {
			case Layouts.HEADER_LAYOUT:
				return (
					<HeaderLayout>
						{props.children}
					</HeaderLayout>
				)
			case Layouts.SIDEBAR_LAYOUT:
				return (
					<SidebarLayout>
						{props.children}
					</SidebarLayout>
				)
			case Layouts.HEADER_SIDEBAR:
				return (
					<HeaderSidebarLayout>
						{props.children}
					</HeaderSidebarLayout>
				)
			case Layouts.SIDEBAR_HEADER:
				return (
					<SidebarHeaderLayout>
						{props.children}
					</SidebarHeaderLayout>
				)
			case Layouts.CONTENT_ONLY:
				return (
					<ContentOnlyLayout>
						{props.children}
					</ContentOnlyLayout>
				)
			default:
				return (
					<SidebarLayout>
						{props.children}
					</SidebarLayout>
				)
		}
	}, [layoutName]);

	return (
		<LayoutContext.Provider value={formMemo}>
			<ToastsProvider>
				<MapLayout>
					{props.children}
				</MapLayout>
{/*				{token && (
					<DevMenu />
				)}*/}
			</ToastsProvider>
		</LayoutContext.Provider>
	)
}

const useLayout = () => useContext(LayoutContext);

export { LayoutContext, useLayout };

export default LayoutProvider